import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery, endpoints, formatErrors } from 'utils/api/endpoints';
import {
  XmlFeedDoc,
  XmlFeedSchemaResult,
  XmlFeedMapping,
  XmlFeedSyncResult,
  XmlFeedWidgetConfigDefaults,
} from './types';
import { ApiResponse } from 'types/ApiResponse';

export const xmlFeedApi = createApi({
  reducerPath: 'xmlFeedApi',
  baseQuery,
  tagTypes: ['XmlFeed'],
  endpoints: (builder) => ({
    // Get current feed configuration
    getFeed: builder.query<ApiResponse<XmlFeedDoc>, void>({
      query: () => ({
        url: endpoints.xmlFeed.getFeed.url,
        method: endpoints.xmlFeed.getFeed.method,
      }),
      providesTags: ['XmlFeed'],
    }),

    // Fetch and parse XML feed to get available fields
    fetchSchema: builder.mutation<ApiResponse<XmlFeedSchemaResult>, { feedUrl: string }>({
      query: (data) => ({
        url: endpoints.xmlFeed.fetchSchema.url,
        method: endpoints.xmlFeed.fetchSchema.method,
        body: data,
      }),
      transformErrorResponse: (response) => formatErrors(response),
    }),

    // Create/update feed configuration with mapping and trigger initial sync
    connect: builder.mutation<ApiResponse<XmlFeedDoc>, {
      feedUrl: string;
      frequency: 'hourly' | 'daily' | 'weekly';
      mapping: XmlFeedMapping;
      mappingMode: 'manual';
      language?: string;
      feedLanguage?: string;
      defaultLanguage?: string;
      widgetConfigDefaults?: XmlFeedWidgetConfigDefaults;
    }>({
      query: (data) => ({
        url: endpoints.xmlFeed.connect.url,
        method: endpoints.xmlFeed.connect.method,
        body: data,
      }),
      invalidatesTags: ['XmlFeed'],
      transformErrorResponse: (response) => formatErrors(response),
    }),

    // Update field mapping without retriggering sync
    updateMapping: builder.mutation<ApiResponse<XmlFeedDoc>, {
      mapping: XmlFeedMapping;
      mappingMode?: 'manual';
      language?: string;
      feedLanguage?: string;
      defaultLanguage?: string;
      widgetConfigDefaults?: XmlFeedWidgetConfigDefaults;
    }>({
      query: (data) => ({
        url: endpoints.xmlFeed.updateMapping.url,
        method: endpoints.xmlFeed.updateMapping.method,
        body: data,
      }),
      invalidatesTags: ['XmlFeed'],
      transformErrorResponse: (response) => formatErrors(response),
    }),

    // Trigger immediate feed sync
    forceSync: builder.mutation<ApiResponse<{
      syncResult: XmlFeedSyncResult;
      feed: XmlFeedDoc;
    }>, void>({
      query: () => ({
        url: endpoints.xmlFeed.forceSync.url,
        method: endpoints.xmlFeed.forceSync.method,
      }),
      invalidatesTags: ['XmlFeed'],
      transformErrorResponse: (response) => formatErrors(response),
    }),

    // Disconnect feed and clear sync data
    disconnect: builder.mutation<ApiResponse<{ message: string }>, void>({
      query: () => ({
        url: endpoints.xmlFeed.disconnect.url,
        method: endpoints.xmlFeed.disconnect.method,
      }),
      invalidatesTags: ['XmlFeed'],
      transformErrorResponse: (response) => formatErrors(response),
    }),
  }),
});

// Export hooks
export const {
  useGetFeedQuery,
  useLazyGetFeedQuery,
  useFetchSchemaMutation,
  useConnectMutation,
  useUpdateMappingMutation,
  useForceSyncMutation,
  useDisconnectMutation,
} = xmlFeedApi;
